import {
  flairOf,
  type IceRacer,
  type IceSnapshot,
  LEVELS,
  levelById,
  levelNumber,
  MAX_RACERS,
  MIN_RACERS,
} from '@xox/shared'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { Lineup, type LineupEntry } from '../components/race/Lineup'
import { PartyLobby } from '../components/race/PartyLobby'
import { IconButton } from '../components/ui'
import { Skater } from './art/Skater'
import { MiniMap } from './LevelCard'
import { LevelPickerSheet } from './LevelPickerSheet'

/** Ice racers as lineup entries (podium and standings use the same mapping). */
export function iceEntries(racers: IceRacer[], hostId: string, me: string | null): LineupEntry[] {
  return racers.map((r) => ({
    id: r.profile.id,
    nickname: r.profile.nickname,
    color: flairOf(r.profile).color,
    isHost: r.profile.id === hostId,
    isMe: r.profile.id === me,
  }))
}

const skaterFigure = (e: LineupEntry) => <Skater color={e.color} className="h-8 drop-shadow" />

/** Joined racers as skaters, plus dashed empty slots showing how many more can (or must) join. */
export function RacerLineup({
  racers,
  hostId,
  me,
  onDark = false,
}: {
  racers: IceRacer[]
  hostId: string
  me: string | null
  onDark?: boolean
}) {
  return (
    <Lineup
      entries={iceEntries(racers, hostId, me)}
      min={MIN_RACERS}
      max={MAX_RACERS}
      figure={skaterFigure}
      onDark={onDark}
      label="Racers in this lobby"
    />
  )
}

/** "#13 Wind Tunnels 1 · par 5" with prev/next and a picker sheet; read-only for non-hosts. */
export function LevelStepper({
  levelId,
  onChange,
  editable,
}: {
  levelId: string
  onChange: (id: string) => void
  editable: boolean
}) {
  const [picking, setPicking] = useState(false)
  const level = levelById(levelId) ?? LEVELS[0]!
  const index = LEVELS.indexOf(level)
  const step = (delta: number) => {
    const next = LEVELS[index + delta]
    if (next) onChange(next.id)
  }
  return (
    <div className="flex items-center gap-2 rounded-2xl bg-slate-100 p-1.5 dark:bg-slate-800">
      {editable && (
        <IconButton label="Previous level" onClick={() => step(-1)} disabled={index <= 0}>
          <ChevronLeft className="size-5" aria-hidden />
        </IconButton>
      )}
      <MiniMap rows={level.rows} className="w-10 shrink-0" />
      <div className="min-w-0 flex-1 text-left">
        <div className="truncate text-sm font-bold">
          <span className="text-slate-400">#{levelNumber(level.id)}</span> {level.name}
        </div>
        <div className="text-xs text-slate-500">
          par {level.par}
          {editable && (
            <>
              {' · '}
              <button
                type="button"
                className="font-semibold text-sky-600 hover:underline dark:text-sky-400"
                onClick={() => setPicking(true)}
              >
                Choose level
              </button>
            </>
          )}
        </div>
      </div>
      {editable && (
        <IconButton
          label="Next level"
          onClick={() => step(1)}
          disabled={index >= LEVELS.length - 1}
        >
          <ChevronRight className="size-5" aria-hidden />
        </IconButton>
      )}
      {editable && (
        <LevelPickerSheet
          open={picking}
          value={level.id}
          onPick={(l) => onChange(l.id)}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  )
}

interface LobbyProps {
  snap: IceSnapshot
  playerId: string | null
  code: string
  pickedLevel: string
  onPickLevel: (id: string) => void
  onStart: () => void
  onJoin: () => void
}

/** Everything a player needs before the race, in the card over the board. */
export function RaceLobby({
  snap,
  playerId,
  code,
  pickedLevel,
  onPickLevel,
  onStart,
  onJoin,
}: LobbyProps) {
  const short = Math.max(0, MIN_RACERS - snap.racers.length)
  return (
    <PartyLobby
      eyebrow={`Round ${snap.round + 1}`}
      title={short > 0 ? 'Waiting in the lobby' : 'Ready to race?'}
      entries={iceEntries(snap.racers, snap.hostId, playerId)}
      min={MIN_RACERS}
      max={MAX_RACERS}
      figure={skaterFigure}
      hostId={snap.hostId}
      playerId={playerId}
      onStart={onStart}
      startLabel="Start race"
      onJoin={onJoin}
      joinLabel="Join the race"
      fullLabel="Race is full — you are watching"
      seatNoun="racer"
      invite={{ code, path: '/ice/r', game: 'ice' }}
    >
      <div className="w-full">
        <LevelStepper
          levelId={pickedLevel}
          onChange={onPickLevel}
          editable={snap.hostId === playerId}
        />
      </div>
    </PartyLobby>
  )
}
